import { graphql } from 'gatsby';
import React, { useState, useRef, useEffect } from 'react';
import Stack from '../../components/Stack/Stack';
import {
    Body1,
    Body2,
    Head3,
    Head6,
    Headline,
} from '../../components/Typography/Typography';
import WithSection from '../../components/WithSection/WithSection';
import Image from '../../components/Image/Image';
import Box from '../../components/Box/Box';
import FeatureTab from '../../components/FeatureTab/FeatureTab';
import Carousel from '../../components/Carousel/Carousel';
import TabsGroup from '../../components/TabsGroup/TabsGroup';
import useViewport from '../../hooks/useViewport';
import { useInView } from 'react-intersection-observer';
import PrismicLink from '../../components/PrismicLink/PrismicLink';
import { gridGap } from '../../components/Grid/Grid';
import Button from '../../components/Button/Button';
import { IoIosArrowForward } from 'react-icons/io';
import { rem } from 'polished';
import { css } from 'styled-components';
import ChevronRight from '../../images/icons/chevron-right.svg';

const CarouselWrapper = ({
    autoplay,
    slider,
    items,
    setActiveSlide,
    ...props
}) => {
    const { isDesktop } = useViewport();

    return (
        <Carousel
            ref={slider}
            dots={false}
            arrows={false}
            autoplay
            autoplaySpeed={5000}
            fade
            pauseOnHover
            pauseOnDotsHover
            pauseOnFocus
            smOptions={{ arrows: false }}
            mdOptions={{ arrows: false }}
            beforeChange={(_, index) => setActiveSlide(index)}
            {...props}
        >
            {items.map(({ image, title, description, text_link, link }, i) => (
                <Box key={i}>
                    <Box>
                        <Image img={image} />
                    </Box>
                    <Box display={{ lg: 'none' }}>
                        <Headline
                            textAlign={{
                                _: 'start',
                                sm: 'center',
                            }}
                            color="neutral.white"
                            mt={{ _: rem(20), sm: rem(32) }}
                        >
                            {title.text}
                        </Headline>
                        <Body2
                            textAlign={{
                                _: 'start',
                                sm: 'center',
                            }}
                            color="primary.300"
                            mt={{ _: rem(16), sm: rem(20) }}
                        >
                            {description}
                        </Body2>
                        {!isDesktop && text_link && (
                            <Box
                                mt={{
                                    _: rem(16),
                                    sm: rem(28),
                                }}
                                display="flex !important"
                                justifyContent={{
                                    _: 'start',
                                    sm: 'center',
                                }}
                            >
                                <PrismicLink link={link}>
                                    <Button variant="white-secondary">
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            css={css`
                                                gap: 10px;
                                            `}
                                        >
                                            <span>{text_link}</span>
                                            <IoIosArrowForward />
                                        </Box>
                                    </Button>
                                </PrismicLink>
                            </Box>
                        )}
                    </Box>
                </Box>
            ))}
        </Carousel>
    );
};
const TimerSection = ({
    data: { primary, items },
    styling: { backgroundVariant: sectionBackground },
}) => {
    const { heading, subheading, cta_text, cta_link, cta_tracking_event } =
        primary;
    const [activeSlide, setActiveSlide] = useState(0);
    const slider = useRef();
    const [carouselRef, inView] = useInView({
        triggerOnce: false,
        threshold: 0.8,
    });
    return (
        <>
            <Box
                gridColumn="1/-1"
                gridRow="1"
                mb={{ _: rem('32px'), md: rem('48px') }}
            >
                <Head3
                    color="neutral.white"
                    textAlign={['start', 'center']}
                    bold
                    py={rem('8px')}
                >
                    {heading.text}
                </Head3>
                <Body1
                    color="neutral.white"
                    textAlign={['start', 'center']}
                    marginTop={rem('4px')}
                    py={rem('8px')}
                >
                    {subheading}
                </Body1>
            </Box>
            <TabsGroup
                items={items}
                activeSlide={activeSlide}
                slider={slider}
                // @ts-ignore
                gridColumn="1/-1"
                gridRow="2"
                sectionBackground={sectionBackground}
            />
            <Box
                display={{ _: 'flex', lg: 'none' }}
                gridRow="3"
                gridColumn="1/-1"
                mt={rem(32)}
                css={css`
                    button {
                        background-color: ${({ theme }) =>
                            theme.colors.primary['1500']};
                    }
                `}
                justifyContent="center"
            >
                <PrismicLink link={cta_link}>
                    <Button
                        size="md"
                        trackingEvent={cta_tracking_event}
                        endIcon={
                            <Box
                                as={ChevronRight}
                                size={rem(20)}
                                color="currentColor"
                            />
                        }
                    >
                        {cta_text}
                    </Button>
                </PrismicLink>
            </Box>
            <Box
                ref={carouselRef}
                marginTop={{ _: rem(32), lg: 0 }}
                display="grid"
                gridTemplateColumns="1fr 1fr 1fr"
                gridRow={{ _: 4, lg: 2 }}
                gridColumn={{ _: '1/-1', md: '2/-2' }}
                gridGap={gridGap}
            >
                <Stack
                    space={rem('18px')}
                    display={{ _: 'none', lg: 'block' }}
                    gridColumn="1"
                >
                    {items.map((item, i) => (
                        <Box key={item.description} marginBottom={['40px', 0]}>
                            <Box
                                onClick={() => {
                                    slider?.current?.slickGoTo(i);
                                }}
                            >
                                <FeatureTab
                                    isActive={activeSlide === i}
                                    item={item}
                                />
                            </Box>
                        </Box>
                    ))}
                    <Box
                        css={css`
                            button {
                                background-color: ${({ theme }) =>
                                    theme.colors.primary['1500']};
                            }
                        `}
                    >
                        <PrismicLink link={cta_link}>
                            <Button
                                size="lg"
                                trackingEvent={cta_tracking_event}
                                fullWidth
                                endIcon={
                                    <Box
                                        as={ChevronRight}
                                        size={rem(20)}
                                        color="currentColor"
                                    />
                                }
                            >
                                {cta_text}
                            </Button>
                        </PrismicLink>
                    </Box>
                </Stack>
                <Box overflow="hidden" gridColumn={{ _: '1/-1', lg: 'span 2' }}>
                    <CarouselWrapper
                        slider={slider}
                        items={items}
                        setActiveSlide={setActiveSlide}
                        inView={inView}
                    />
                </Box>
            </Box>
        </>
    );
};

// TODO: convert to fragments
export const _ = graphql`
    fragment TIMER_SECTION on PrismicTimerCarouselDefault {
        primary {
            heading {
                text
            }
            subheading
            cta_text
            cta_tracking_event
            cta_link {
                ...PRISMIC_LINK_FRAGMENT
            }
        }
        items {
            description
            icon {
                url
                alt
            }
            image {
                gatsbyImageData(layout: CONSTRAINED, height: 1200)
                alt
                url
                dimensions {
                    height
                    width
                }
            }
            title {
                text
            }
        }
    }
`;

export default WithSection(TimerSection);
